import { getWheelDisplayName } from "../../utils/helpers";
import {
  computeSegmentLabel,
  orderSegmentsByPosition,
  getSegmentTitle,
} from "../../utils/segmentLabels";

/**
 * segmentPicker.helpers — pure logic behind the "Add to Segment" picker.
 *
 * Kelly (VP Sales), QA-52/QA-53, verbatim:
 *
 *   "if drag and drop is not feasible then right underneath the captured item
 *    it should say 'add to segment'. And if there are a bunch of segments
 *    already in the wheel all of the segments should drop down and you get to
 *    pick the segment you want to drop it in."
 *
 * The picker is NOT merely a fallback for a browser that cannot drag. It is the
 * only route reachable by keyboard and by screen reader, and it is the primary
 * route on touch — so this logic is load-bearing on its own, and is kept pure
 * and separately asserted rather than buried in the dialog's render.
 *
 * A Segment is `wheel_section` in the API and `Section` in the backend model.
 */

/** How long a segment name may run before the picker truncates it. */
export const SEGMENT_NAME_MAX = 40;

/**
 * Build the picker's option list from a wheel's `wheel_section` array.
 *
 * - An unnamed segment is labelled via the WHEEL's own display-name helper
 *   (utils/segmentLabels `computeSegmentLabel`), NOT its raw id. Backend BAKES
 *   `Segment ${id}` into an unnamed segment's title at creation, so the raw
 *   title of an unnamed segment reads "Segment 21320". The wheel never shows
 *   that: it detects the baked placeholder (PLACEHOLDER_TITLE_PATTERN) and
 *   renumbers to the segment's positional "Segment N". The picker MUST use the
 *   same helper so the two surfaces agree — otherwise the picker lists raw DB
 *   ids while the wheel beside it shows Segment 1..9 (QA-1094).
 * - The row also carries the segment's COLOUR (settings.color) so the picker
 *   can show the same colour swatch the wheel wedge uses (QA-1095).
 * - A row with no id is DROPPED: it cannot be an insert target, and offering it
 *   would produce a click that silently does nothing — the exact failure this
 *   whole feature exists to remove.
 * - Names are flattened through getWheelDisplayName because segment titles can
 *   carry stored Froala HTML; the flattened text is then handed to the wheel
 *   helper so a named "<p>Gold</p>" stays "Gold" and a baked "Segment 21320"
 *   is still recognised as a placeholder.
 */
export const segmentOptions = (wheelSections) => {
  if (!Array.isArray(wheelSections)) return [];
  // Normalise each present section: flatten any Froala HTML in its title to
  // plain text and expose that as the top-level `title` (blanking settings.title
  // so getSegmentTitle reads the flattened value). id/position/settings.color
  // are preserved untouched for numbering + the colour swatch.
  const present = wheelSections
    .map((section) => {
      if (!section || section.id === undefined || section.id === null) return null;
      const rawTitle = getSegmentTitle(section);
      const flatTitle = rawTitle ? getWheelDisplayName(rawTitle) : "";
      return {
        ...section,
        title: flatTitle,
        settings: { ...(section.settings || {}), title: "" },
      };
    })
    .filter(Boolean);

  // Number unnamed segments the SAME way the wheel does — positionally, in wheel
  // order, among the unnamed only.
  const ordered = orderSegmentsByPosition(present);

  return present.map((section, index) => ({
    id: section.id,
    index,
    name: computeSegmentLabel(section, ordered),
    color: (section.settings && section.settings.color) || null,
  }));
};

/** Does this wheel have anywhere to put a capture? */
export const hasSegments = (wheelSections) => segmentOptions(wheelSections).length > 0;

/**
 * The default target. The FIRST segment, matching the approved
 * `capture-elevated` deck ("First Segment (default)") so the two capture->
 * segment surfaces preselect the same thing rather than disagreeing.
 */
export const defaultSegmentId = (wheelSections) => {
  const options = segmentOptions(wheelSections);
  return options.length ? options[0].id : null;
};

/**
 * Truncate a long segment name for display. Returns the name unchanged when it
 * fits, so the common case never carries an ellipsis. The FULL name is still
 * given to the option's accessible label by the dialog — a screen-reader user
 * must not be handed the truncated string.
 */
export const truncateSegmentName = (name, max = SEGMENT_NAME_MAX) => {
  const value = typeof name === "string" ? name : "";
  if (value.length <= max) return value;
  return `${value.slice(0, max - 1).trimEnd()}…`;
};

/** Is this a real, selectable target on this wheel? Guards a stale selection. */
export const isValidSegmentTarget = (wheelSections, segmentId) =>
  segmentOptions(wheelSections).some((option) => option.id === segmentId);

/**
 * What the picker says when there is nowhere to put the capture. A wheel with
 * no segments must SAY so — an empty dropdown is a dead end that reads as a
 * broken control.
 */
export const NO_SEGMENTS_MESSAGE =
  "This Wheel has no Segments yet. Add a Segment first, then add this capture to it.";
